import { oc } from "@orpc/contract";
import { z } from "zod";

// ─── Shared schemas ─────────────────────────────────────────────────────────

export const OptionActionSchema = z.enum([
  "transfer",
  "buy",
  "buy_and_transfer",
]);
export const OptionSourceSchema = z.enum(["engine", "user"]);
export const OptionLegTypeSchema = z.enum(["buy", "transfer"]);
// Warehouse type of a transfer's source, so the UI can label a transfer as
// coming from the destination's hub vs. a peer (sibling) branch. `branch` =
// sibling-branch transfer; `hub` = hub transfer.
export const TransferSourceWarehouseTypeSchema = z
  .enum(["hub", "branch"])
  .nullable();
export const OptionLegStatusSchema = z.enum([
  "planned",
  "created",
  "cancelled",
]);

// One leg of a composed option plan (a buy → PO line, or a transfer → TO line).
export const SuggestionOptionLegSchema = z.object({
  legId: z.string().uuid(),
  legType: OptionLegTypeSchema,
  rank: z.number(),
  status: OptionLegStatusSchema,
  quantity: z.number().nullable(),
  unitCost: z.number().nullable(),
  estimatedCost: z.number().nullable(),
  freightEstimate: z.number().nullable(),
  estimatedLeadTimeDays: z.number().nullable(),
  sourceWarehouseId: z.string().uuid().nullable(),
  sourceWarehouseName: z.string().nullable(),
  sourceWarehouseType: TransferSourceWarehouseTypeSchema,
  shipToWarehouseId: z.string().uuid().nullable(),
  shipToWarehouseName: z.string().nullable(),
  supplierId: z.string().uuid().nullable(),
  supplierName: z.string().nullable(),
  vendorMoqQuantity: z.number().nullable(),
  vendorMovAmount: z.number().nullable(),
  vendorPrepaidFreightThreshold: z.number().nullable(),
  belowMinimum: z.boolean(),
  minimumGapAmount: z.number().nullable(),
});
export type SuggestionOptionLeg = z.infer<typeof SuggestionOptionLegSchema>;
export const SuggestionStatusSchema = z.enum([
  "pending",
  "accepted",
  "rejected",
  "ordered",
]);
export const SuggestionTriggerSchema = z.enum(["deficit", "sales_order"]);
export const SuggestionPrioritySchema = z.enum([
  "critical",
  "urgent",
  "standard",
  "fill",
]);

// ─── Stage 1: Supplier summaries ────────────────────────────────────────────

export const SupplierSummaryRowSchema = z.object({
  supplierId: z.string().uuid(),
  supplierName: z.string(),
  openSuggestionCount: z.number(),
  salesOrderSuggestionCount: z.number(),
  totalNeededQuantity: z.number(),
  totalEstimatedCost: z.number(),
  movThreshold: z.number().nullable(),
  cartTotal: z.number(),
  belowMinimumCount: z.number(),
});

export type SupplierSummaryRow = z.infer<typeof SupplierSummaryRowSchema>;

export const ListSupplierSummariesInputSchema = z.object({
  status: SuggestionStatusSchema.optional(),
  // Legacy single-division filter. Prefer `divisionIds` for new callers; both
  // are accepted and merged server-side (single → array of 1).
  divisionId: z.string().uuid().optional(),
  divisionIds: z.array(z.string().uuid()).optional(),
  // Destination-warehouse filter, scoped to the selected divisions. Empty/omitted
  // means "all warehouses in scope". Filters on s.destination_warehouse_id.
  warehouseIds: z.array(z.string().uuid()).optional(),
  // Board-wide "Show trashed": include rejected suggestions alongside pending.
  includeTrashed: z.boolean().optional(),
  priority: SuggestionPrioritySchema.optional(),
  search: z.string().optional(),
  batchId: z.string().uuid().optional(),
  page: z.number().int().min(1).optional().default(1),
  pageSize: z.number().int().min(1).max(500).optional().default(50),
});

export const ListSupplierSummariesResponseSchema = z.object({
  rows: z.array(SupplierSummaryRowSchema),
  totalRows: z.number(),
});

export type ListSupplierSummariesInput = z.infer<
  typeof ListSupplierSummariesInputSchema
>;
export type ListSupplierSummariesResponse = z.infer<
  typeof ListSupplierSummariesResponseSchema
>;

// ─── Stage 2: Supplier detail (suggestions for one supplier) ────────────────

export const SupplierSuggestionRowSchema = z.object({
  suggestionId: z.string().uuid(),
  productId: z.string().uuid(),
  productSku: z.string().nullable(),
  productName: z.string(),
  destinationWarehouseId: z.string().uuid(),
  destinationWarehouseName: z.string(),
  trigger: SuggestionTriggerSchema,
  priority: SuggestionPrioritySchema,
  status: SuggestionStatusSchema,
  neededQuantity: z.number(),
  currentOnHand: z.number(),
  currentNetPosition: z.number(),
  policyMin: z.number().nullable(),
  policyMax: z.number().nullable(),
  popularityCode: z.string().nullable(),
  topOption: z.object({
    optionId: z.string().uuid(),
    action: OptionActionSchema,
    buyQuantity: z.number().nullable(),
    unitCost: z.number().nullable(),
    totalCost: z.number().nullable(),
    rationale: z.string().nullable(),
    belowMinimum: z.boolean(),
    minimumGapAmount: z.number().nullable(),
  }),
});

export type SupplierSuggestionRow = z.infer<typeof SupplierSuggestionRowSchema>;

export const GetSupplierDetailInputSchema = z.object({
  supplierId: z.string().uuid(),
  status: SuggestionStatusSchema.optional(),
  search: z.string().optional(),
  page: z.number().int().min(1).optional().default(1),
  pageSize: z.number().int().min(1).max(500).optional().default(50),
});

export const SupplierConstraintSchema = z.object({
  warehouseId: z.string().uuid(),
  warehouseName: z.string(),
  productGroup: z.string().nullable(),
  constraintValue: z.number().nullable(),
  constraintUom: z.string().nullable(),
  isPreferred: z.boolean(),
});

export type SupplierConstraint = z.infer<typeof SupplierConstraintSchema>;

export const GetSupplierDetailResponseSchema = z.object({
  supplierId: z.string().uuid(),
  supplierName: z.string(),
  movThreshold: z.number().nullable(),
  cartTotal: z.number(),
  constraints: z.array(SupplierConstraintSchema),
  rows: z.array(SupplierSuggestionRowSchema),
  totalRows: z.number(),
});

export type GetSupplierDetailInput = z.infer<
  typeof GetSupplierDetailInputSchema
>;
export type GetSupplierDetailResponse = z.infer<
  typeof GetSupplierDetailResponseSchema
>;

// ─── Stage 3: Suggestion detail (all options for one suggestion) ────────────

export const SuggestionOptionSchema = z.object({
  optionId: z.string().uuid(),
  source: OptionSourceSchema,
  action: OptionActionSchema,
  rank: z.number(),
  supplierId: z.string().uuid().nullable(),
  supplierName: z.string().nullable(),
  shipToWarehouseId: z.string().uuid().nullable(),
  shipToWarehouseName: z.string().nullable(),
  buyQuantity: z.number().nullable(),
  unitCost: z.number().nullable(),
  totalCost: z.number().nullable(),
  estimatedFreightCost: z.number().nullable(),
  vendorMoqQuantity: z.number().nullable(),
  vendorIncrementQuantity: z.number().nullable(),
  vendorMovAmount: z.number().nullable(),
  vendorPrepaidFreightThreshold: z.number().nullable(),
  belowMinimum: z.boolean(),
  minimumGapAmount: z.number().nullable(),
  sourceWarehouseId: z.string().uuid().nullable(),
  sourceWarehouseName: z.string().nullable(),
  sourceWarehouseType: TransferSourceWarehouseTypeSchema,
  transferQuantity: z.number().nullable(),
  transferFreightEstimate: z.number().nullable(),
  rationale: z.string().nullable(),
  estimatedLeadTimeDays: z.number().nullable(),
  createdByUserId: z.string().uuid().nullable(),
  createdByUserName: z.string().nullable(),
  createdAt: z.string(),
  // The composed plan's legs. Flat fields above are derived from the
  // representative buy/transfer leg for the existing card; `legs` is the full
  // breakdown (supports multi-leg plans, e.g. two transfers).
  legs: z.array(SuggestionOptionLegSchema),
});

export type SuggestionOption = z.infer<typeof SuggestionOptionSchema>;

export const GetSuggestionDetailInputSchema = z.object({
  suggestionId: z.string().uuid(),
});

export const GetSuggestionDetailResponseSchema = z.object({
  suggestionId: z.string().uuid(),
  destinationWarehouseId: z.string().uuid(),
  destinationWarehouseName: z.string(),
  productId: z.string().uuid(),
  // Variant of the destination policy this suggestion is for. Present for
  // deficit (min/max) suggestions — needed to address the inventory-policy
  // override; null for some SO-driven lines.
  productVariantId: z.string().uuid().nullable(),
  productSku: z.string().nullable(),
  productName: z.string(),
  trigger: SuggestionTriggerSchema,
  priority: SuggestionPrioritySchema,
  status: SuggestionStatusSchema,
  neededQuantity: z.number(),
  currentOnHand: z.number(),
  currentNetPosition: z.number(),
  policyMin: z.number().nullable(),
  policyMax: z.number().nullable(),
  // Forecasting-engine outputs on the destination policy (null until the engine
  // has run for this SKU×warehouse). safetyStock / reorderPoint are the
  // demand-driven buffer + reorder level; orderUpToLevel is the target the
  // deficit fills to (what the quantity reconciles against: needed = orderUpTo −
  // net); targetAvailability is the service level; forecastCalculatedAt = when
  // they were computed.
  safetyStock: z.number().nullable(),
  reorderPoint: z.number().nullable(),
  orderUpToLevel: z.number().nullable(),
  targetAvailability: z.number().nullable(),
  forecastCalculatedAt: z.string().nullable(),
  // Buyer's per-item source decision (INITINFC giparent1) on the destination
  // policy: 'transfer' (from buyerSourceWarehouse), 'buy', or null (unset).
  // buyerSourceHonored = the recommended/effective option actually satisfies it
  // (false = the engine fell back, e.g. the pinned source was out of stock).
  buyerSourceType: z.enum(["transfer", "buy"]).nullable(),
  buyerSourceWarehouseId: z.string().uuid().nullable(),
  buyerSourceWarehouseName: z.string().nullable(),
  buyerSourceHonored: z.boolean(),
  chosenOptionId: z.string().uuid().nullable(),
  reviewedByUserName: z.string().nullable(),
  reviewedAt: z.string().nullable(),
  batchId: z.string().uuid(),
  generatedAt: z.string(),
  // Triggering sales-order context — null for deficit-triggered suggestions.
  // Exposed here so the slidein can embed the SO detail inline instead of
  // opening a separate SalesOrderDetailModal.
  triggerSalesOrderId: z.string().uuid().nullable(),
  triggerSalesOrderNumber: z.string().nullable(),
  triggerSalesOrderLineItemId: z.string().uuid().nullable(),
  options: z.array(SuggestionOptionSchema),
});

export type GetSuggestionDetailInput = z.infer<
  typeof GetSuggestionDetailInputSchema
>;
export type GetSuggestionDetailResponse = z.infer<
  typeof GetSuggestionDetailResponseSchema
>;

// ─── Transfer summaries (suggestions with transfer-only top option) ─────────

export const TransferSummaryRowSchema = z.object({
  suggestionId: z.string().uuid(),
  // Null on a v3-only suggestion: procurement is migrating onto ProductV3
  // (whose key IS the SKU) and no longer writes the im_products_v2 id. Use
  // productSku, which falls back to that key, as the display handle.
  productId: z.string().uuid().nullable(),
  productSku: z.string().nullable(),
  productName: z.string(),
  destinationWarehouseId: z.string().uuid(),
  destinationWarehouseName: z.string(),
  sourceWarehouseId: z.string().uuid(),
  sourceWarehouseName: z.string(),
  priority: SuggestionPrioritySchema,
  status: SuggestionStatusSchema,
  neededQuantity: z.number(),
  transferQuantity: z.number(),
  transferFreightEstimate: z.number().nullable(),
  rationale: z.string().nullable(),
});

export type TransferSummaryRow = z.infer<typeof TransferSummaryRowSchema>;

export const ListTransferSummariesInputSchema = z.object({
  status: SuggestionStatusSchema.optional(),
  divisionId: z.string().uuid().optional(),
  divisionIds: z.array(z.string().uuid()).optional(),
  // Destination-warehouse filter (see ListSupplierSummariesInputSchema).
  warehouseIds: z.array(z.string().uuid()).optional(),
  search: z.string().optional(),
  page: z.number().int().min(1).optional().default(1),
  pageSize: z.number().int().min(1).max(500).optional().default(50),
});

export const ListTransferSummariesResponseSchema = z.object({
  rows: z.array(TransferSummaryRowSchema),
  totalRows: z.number(),
});

export type ListTransferSummariesInput = z.infer<
  typeof ListTransferSummariesInputSchema
>;
export type ListTransferSummariesResponse = z.infer<
  typeof ListTransferSummariesResponseSchema
>;

// ─── No-source suggestions (no fulfillment option found) ────────────────────

export const NoSourceRowSchema = z.object({
  suggestionId: z.string().uuid(),
  // Null on a v3-only suggestion: procurement is migrating onto ProductV3
  // (whose key IS the SKU) and no longer writes the im_products_v2 id. Use
  // productSku, which falls back to that key, as the display handle.
  productId: z.string().uuid().nullable(),
  productSku: z.string().nullable(),
  productName: z.string(),
  destinationWarehouseId: z.string().uuid(),
  destinationWarehouseName: z.string(),
  trigger: SuggestionTriggerSchema,
  triggerSalesOrderId: z.string().uuid().nullable(),
  triggerSalesOrderNumber: z.string().nullable(),
  priority: SuggestionPrioritySchema,
  status: SuggestionStatusSchema,
  neededQuantity: z.number(),
  currentOnHand: z.number(),
  currentNetPosition: z.number(),
});

export type NoSourceRow = z.infer<typeof NoSourceRowSchema>;

export const ListNoSourceSuggestionsInputSchema = z.object({
  status: SuggestionStatusSchema.optional(),
  divisionId: z.string().uuid().optional(),
  divisionIds: z.array(z.string().uuid()).optional(),
  // Destination-warehouse filter (see ListSupplierSummariesInputSchema).
  warehouseIds: z.array(z.string().uuid()).optional(),
  includeTrashed: z.boolean().optional(),
  search: z.string().optional(),
  page: z.number().int().min(1).optional().default(1),
  pageSize: z.number().int().min(1).max(500).optional().default(50),
});

export const ListNoSourceSuggestionsResponseSchema = z.object({
  rows: z.array(NoSourceRowSchema),
  totalRows: z.number(),
});

export type ListNoSourceSuggestionsInput = z.infer<
  typeof ListNoSourceSuggestionsInputSchema
>;
export type ListNoSourceSuggestionsResponse = z.infer<
  typeof ListNoSourceSuggestionsResponseSchema
>;

// ─── Vendorless-transfer board: source-warehouse cards (tier-1) ─────────────

// One card per source warehouse (the warehouse shipping the stock) — the
// "supplier" analog for pure transfers, which have no vendor.
export const SourceTransferSummaryRowSchema = z.object({
  sourceWarehouseId: z.string().uuid(),
  sourceWarehouseName: z.string(),
  sourceWarehouseType: z.string().nullable(),
  lineCount: z.number(),
  soTriggeredCount: z.number(),
  deficitCount: z.number(),
  // Distinct destination warehouses these transfers go to (= cart count).
  destinationCount: z.number(),
  totalQuantity: z.number(),
});

export type SourceTransferSummaryRow = z.infer<
  typeof SourceTransferSummaryRowSchema
>;

export const ListVendorlessTransferSummariesInputSchema = z.object({
  status: SuggestionStatusSchema.optional(),
  divisionId: z.string().uuid().optional(),
  divisionIds: z.array(z.string().uuid()).optional(),
  // Destination-warehouse filter (see ListSupplierSummariesInputSchema).
  warehouseIds: z.array(z.string().uuid()).optional(),
  includeTrashed: z.boolean().optional(),
  search: z.string().optional(),
});

export const ListVendorlessTransferSummariesResponseSchema = z.object({
  rows: z.array(SourceTransferSummaryRowSchema),
  totalRows: z.number(),
});

export type ListVendorlessTransferSummariesInput = z.infer<
  typeof ListVendorlessTransferSummariesInputSchema
>;
export type ListVendorlessTransferSummariesResponse = z.infer<
  typeof ListVendorlessTransferSummariesResponseSchema
>;

// ─── Supplier summaries with nested cart breakdowns ─────────────────────────

export const CartSummarySchema = z.object({
  shipToWarehouseId: z.string().uuid(),
  shipToWarehouseName: z.string(),
  shipToWarehouseType: z.string().nullable(),
  lineCount: z.number(),
  cartTotal: z.number(),
  movThreshold: z.number().nullable(),
  freightThreshold: z.number().nullable(),
  soTriggeredCount: z.number(),
  belowMinimumCount: z.number(),
});

export type CartSummary = z.infer<typeof CartSummarySchema>;

export const SupplierWithCartsSummaryRowSchema = z.object({
  supplierId: z.string().uuid(),
  supplierName: z.string(),
  openSuggestionCount: z.number(),
  totalEstimatedCost: z.number(),
  cartCount: z.number(),
  soTriggeredCount: z.number(),
  deficitCount: z.number(),
  belowMinimumCount: z.number(),
  // Lines under this vendor whose recommended action is a transfer (no buy
  // spend). They count toward openSuggestionCount but not toward any cart/MOV.
  transferCount: z.number(),
  // POTF (product-on-the-fly) line count, joined via the suggestion's trigger
  // soli's `metadata.isProductOnTheFly`. Belt's POTF card sorts by this.
  potfLineCount: z.number(),
  // AS400 division codes (e.g. ['RA', 'WG']) covering every destination
  // warehouse this supplier's suggestions touch. Sorted alphabetically.
  // Rendered as small pills on each supplier card so buyers can see the
  // division at a glance when multi-division is selected.
  divisionCodes: z.array(z.string()),
  carts: z.array(CartSummarySchema),
});

export type SupplierWithCartsSummaryRow = z.infer<
  typeof SupplierWithCartsSummaryRowSchema
>;

// A synthetic row representing aggregated vendorless-transfer suggestions
// (resolver bucket=`vendorless` — pure transfers with no buy vendor anywhere
// in their options). Rendered at the bottom of the Actionable list as a single
// "Vendorless transfers" card, treated by the sort stack like any other row.
export const VendorlessTransfersRowSchema = z.object({
  lineCount: z.number(),
  soTriggeredCount: z.number(),
  deficitCount: z.number(),
  potfLineCount: z.number(),
});

export type VendorlessTransfersRow = z.infer<
  typeof VendorlessTransfersRowSchema
>;

export const ListSupplierSummariesWithCartsResponseSchema = z.object({
  rows: z.array(SupplierWithCartsSummaryRowSchema),
  totalRows: z.number(),
  // null when no vendorless transfers exist in scope.
  vendorlessRow: VendorlessTransfersRowSchema.nullable(),
});

export type ListSupplierSummariesWithCartsResponse = z.infer<
  typeof ListSupplierSummariesWithCartsResponseSchema
>;

// ─── Cart lines (suggestions within a supplier × ship-to cart) ──────────────

export const CartLineRowSchema = z.object({
  suggestionId: z.string().uuid(),
  productId: z.string().uuid(),
  productSku: z.string().nullable(),
  productName: z.string(),
  destinationWarehouseId: z.string().uuid(),
  destinationWarehouseName: z.string(),
  trigger: SuggestionTriggerSchema,
  triggerSalesOrderId: z.string().uuid().nullable(),
  triggerSalesOrderNumber: z.string().nullable(),
  priority: SuggestionPrioritySchema,
  status: SuggestionStatusSchema,
  neededQuantity: z.number(),
  currentOnHand: z.number(),
  currentNetPosition: z.number(),
  policyMin: z.number().nullable(),
  policyMax: z.number().nullable(),
  topOption: z.object({
    optionId: z.string().uuid(),
    action: OptionActionSchema,
    buyQuantity: z.number().nullable(),
    unitCost: z.number().nullable(),
    totalCost: z.number().nullable(),
    rationale: z.string().nullable(),
    belowMinimum: z.boolean(),
    minimumGapAmount: z.number().nullable(),
    transferDestinationWarehouseName: z.string().nullable(),
    transferQuantity: z.number().nullable(),
  }),
});

export type CartLineRow = z.infer<typeof CartLineRowSchema>;

export const GetCartLinesInputSchema = z.object({
  supplierId: z.string().uuid(),
  shipToWarehouseId: z.string().uuid(),
  status: SuggestionStatusSchema.optional(),
  search: z.string().optional(),
  page: z.number().int().min(1).optional().default(1),
  pageSize: z.number().int().min(1).max(500).optional().default(50),
});

export type GetCartLinesInput = z.infer<typeof GetCartLinesInputSchema>;

export const GetCartLinesResponseSchema = z.object({
  supplierId: z.string().uuid(),
  supplierName: z.string(),
  shipToWarehouseId: z.string().uuid(),
  shipToWarehouseName: z.string(),
  cartTotal: z.number(),
  movThreshold: z.number().nullable(),
  freightThreshold: z.number().nullable(),
  rows: z.array(CartLineRowSchema),
  totalRows: z.number(),
});

export type GetCartLinesResponse = z.infer<typeof GetCartLinesResponseSchema>;

// ─── Supplier assignments (all lines + carts for one supplier) ──────────────

export const CartAssignmentSchema = z.object({
  shipToWarehouseId: z.string().uuid(),
  shipToWarehouseName: z.string(),
  shipToWarehouseType: z.string().nullable(),
  movThreshold: z.number().nullable(),
  freightThreshold: z.number().nullable(),
});

export type CartAssignment = z.infer<typeof CartAssignmentSchema>;

export const AssignmentLineSchema = z.object({
  suggestionId: z.string().uuid(),
  // Null on a v3-only suggestion: procurement is migrating onto ProductV3
  // (whose key IS the SKU) and no longer writes the im_products_v2 id. Use
  // productSku, which falls back to that key, as the display handle.
  productId: z.string().uuid().nullable(),
  productSku: z.string().nullable(),
  productName: z.string(),
  destinationWarehouseId: z.string().uuid(),
  destinationWarehouseName: z.string(),
  destinationWarehouseType: z.string().nullable(),
  destinationWarehouseHubName: z.string().nullable(),
  trigger: SuggestionTriggerSchema,
  triggerSalesOrderId: z.string().uuid().nullable(),
  triggerSalesOrderNumber: z.string().nullable(),
  priority: SuggestionPrioritySchema,
  status: SuggestionStatusSchema,
  neededQuantity: z.number(),
  currentOnHand: z.number(),
  currentNetPosition: z.number(),
  reservedQuantity: z.number(),
  policyMin: z.number().nullable(),
  policyMax: z.number().nullable(),
  popularityCode: z.string().nullable(),
  stockStatus: z.string().nullable(),
  shipToWarehouseId: z.string().uuid().nullable(),
  // Engine's committed action for this line — null when the line is a fill
  // candidate (deficit trigger without an explicit chosen option). Used by the
  // UI to dim rows that are not actually committed to be ordered.
  recommendedAction: OptionActionSchema.nullable(),
  topOption: z
    .object({
      optionId: z.string().uuid(),
      action: OptionActionSchema,
      buyQuantity: z.number().nullable(),
      vendorMoqQuantity: z.number().nullable(),
      unitCost: z.number().nullable(),
      totalCost: z.number().nullable(),
      rationale: z.string().nullable(),
      belowMinimum: z.boolean(),
      minimumGapAmount: z.number().nullable(),
      transferDestinationWarehouseName: z.string().nullable(),
      transferQuantity: z.number().nullable(),
    })
    .nullable(),
});

export type AssignmentLine = z.infer<typeof AssignmentLineSchema>;

export const GetSupplierAssignmentsInputSchema = z.object({
  supplierId: z.string().uuid(),
  divisionId: z.string().uuid().optional(),
  divisionIds: z.array(z.string().uuid()).optional(),
  // Destination-warehouse filter (see ListSupplierSummariesInputSchema).
  warehouseIds: z.array(z.string().uuid()).optional(),
  includeTrashed: z.boolean().optional(),
  status: SuggestionStatusSchema.optional(),
});

export type GetSupplierAssignmentsInput = z.infer<
  typeof GetSupplierAssignmentsInputSchema
>;

export const GetSupplierAssignmentsResponseSchema = z.object({
  supplierId: z.string().uuid(),
  supplierName: z.string(),
  carts: z.array(CartAssignmentSchema),
  lines: z.array(AssignmentLineSchema),
});

export type GetSupplierAssignmentsResponse = z.infer<
  typeof GetSupplierAssignmentsResponseSchema
>;

// ─── Source-warehouse transfer assignments (tier-2 for the transfer board) ──

export const GetSourceTransferAssignmentsInputSchema = z.object({
  sourceWarehouseId: z.string().uuid(),
  divisionId: z.string().uuid().optional(),
  divisionIds: z.array(z.string().uuid()).optional(),
  warehouseIds: z.array(z.string().uuid()).optional(),
  includeTrashed: z.boolean().optional(),
  status: SuggestionStatusSchema.optional(),
});

// Reuses CartAssignment + AssignmentLine so the frontend renders transfers with
// the same cart components as the supplier board. Carts are keyed on the
// destination warehouse; each line's `shipToWarehouseId` is its destination.
export const GetSourceTransferAssignmentsResponseSchema = z.object({
  sourceWarehouseId: z.string().uuid(),
  sourceWarehouseName: z.string(),
  carts: z.array(CartAssignmentSchema),
  lines: z.array(AssignmentLineSchema),
});

export type GetSourceTransferAssignmentsInput = z.infer<
  typeof GetSourceTransferAssignmentsInputSchema
>;
export type GetSourceTransferAssignmentsResponse = z.infer<
  typeof GetSourceTransferAssignmentsResponseSchema
>;

// ─── Apply option (set chosen fulfillment option for a suggestion) ──────────

export const ApplyOptionInputSchema = z.object({
  suggestionId: z.string().uuid(),
  // null = clear the selection (revert to the engine's recommended default).
  optionId: z.string().uuid().nullable(),
});

export type ApplyOptionInput = z.infer<typeof ApplyOptionInputSchema>;

export const ApplyOptionResponseSchema = z.object({
  suggestionId: z.string().uuid(),
  chosenOptionId: z.string().uuid().nullable(),
});

export type ApplyOptionResponse = z.infer<typeof ApplyOptionResponseSchema>;

// ─── Trash / restore a suggestion (set status pending ↔ rejected) ───────────

export const SetSuggestionStatusInputSchema = z.object({
  suggestionId: z.string().uuid(),
  // 'rejected' trashes the line (hidden from the board); 'pending' restores it.
  // Only these two transitions are allowed — never touches accepted/ordered.
  status: z.enum(["pending", "rejected"]),
});

export type SetSuggestionStatusInput = z.infer<
  typeof SetSuggestionStatusInputSchema
>;

export const SetSuggestionStatusResponseSchema = z.object({
  suggestionId: z.string().uuid(),
  status: z.enum(["pending", "rejected"]),
});

export type SetSuggestionStatusResponse = z.infer<
  typeof SetSuggestionStatusResponseSchema
>;

// ─── Override (buyer adds a custom user-authored option) ────────────────────

export const OverrideOptionInputSchema = z
  .object({
    suggestionId: z.string().uuid(),
    action: OptionActionSchema,
    supplierId: z.string().uuid().nullable().optional(),
    shipToWarehouseId: z.string().uuid().nullable().optional(),
    buyQuantity: z.number().nonnegative().nullable().optional(),
    unitCost: z.number().nonnegative().nullable().optional(),
    sourceWarehouseId: z.string().uuid().nullable().optional(),
    transferQuantity: z.number().nonnegative().nullable().optional(),
    // Whether the new option becomes the suggestion's chosen option (default true).
    setChosen: z.boolean().optional().default(true),
  })
  .superRefine((v, ctx) => {
    const isBuy = v.action === "buy" || v.action === "buy_and_transfer";
    const isTransfer =
      v.action === "transfer" || v.action === "buy_and_transfer";
    // Buy legs need a vendor + quantity (ship-to defaults to the suggestion's
    // destination server-side). Transfer legs need a source + quantity. Missing
    // fields would persist an option the board can't place.
    if (isBuy && v.supplierId == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["supplierId"],
        message: "supplierId is required for buy / buy_and_transfer",
      });
    }
    if (isBuy && (v.buyQuantity == null || v.buyQuantity <= 0)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["buyQuantity"],
        message: "buyQuantity is required for buy / buy_and_transfer",
      });
    }
    if (isTransfer && v.sourceWarehouseId == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["sourceWarehouseId"],
        message:
          "sourceWarehouseId is required for transfer / buy_and_transfer",
      });
    }
    if (isTransfer && (v.transferQuantity == null || v.transferQuantity <= 0)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["transferQuantity"],
        message: "transferQuantity is required for transfer / buy_and_transfer",
      });
    }
  });

export type OverrideOptionInput = z.infer<typeof OverrideOptionInputSchema>;

export const OverrideOptionResponseSchema = z.object({
  suggestionId: z.string().uuid(),
  optionId: z.string().uuid(),
  chosenOptionId: z.string().uuid().nullable(),
});

export type OverrideOptionResponse = z.infer<
  typeof OverrideOptionResponseSchema
>;

// ─── Composite plan builder (multi-leg buyer override) ─────────────────────

export const ComposePlanLegSchema = z
  .object({
    legType: OptionLegTypeSchema,
    quantity: z.number().positive(),
    // buy fields
    supplierId: z.string().uuid().nullable().optional(),
    shipToWarehouseId: z.string().uuid().nullable().optional(),
    unitCost: z.number().nonnegative().nullable().optional(),
    // transfer fields
    sourceWarehouseId: z.string().uuid().nullable().optional(),
  })
  .superRefine((v, ctx) => {
    if (v.legType === "buy" && !v.supplierId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["supplierId"],
        message: "buy leg requires supplierId",
      });
    }
    if (v.legType === "transfer" && !v.sourceWarehouseId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["sourceWarehouseId"],
        message: "transfer leg requires sourceWarehouseId",
      });
    }
  });

export const ComposePlanInputSchema = z.object({
  suggestionId: z.string().uuid(),
  legs: z.array(ComposePlanLegSchema).min(1),
  setChosen: z.boolean().optional().prefault(true),
});

export type ComposePlanInput = z.infer<typeof ComposePlanInputSchema>;
export type ComposePlanLeg = z.infer<typeof ComposePlanLegSchema>;

export const ComposePlanResponseSchema = z.object({
  suggestionId: z.string().uuid(),
  optionId: z.string().uuid(),
  chosenOptionId: z.string().uuid().nullable(),
});

export type ComposePlanResponse = z.infer<typeof ComposePlanResponseSchema>;

// ─── Fulfillment candidates (override pickers) ──────────────────────────────

export const FulfillmentCandidatesInputSchema = z.object({
  productId: z.string().uuid(),
  destinationWarehouseId: z.string().uuid(),
});

export type FulfillmentCandidatesInput = z.infer<
  typeof FulfillmentCandidatesInputSchema
>;

export const FulfillmentCandidatesResponseSchema = z.object({
  transferSources: z.array(
    z.object({
      warehouseId: z.string().uuid(),
      warehouseName: z.string(),
      onHandQuantity: z.number(),
    }),
  ),
  vendors: z.array(
    z.object({
      supplierId: z.string().uuid(),
      supplierName: z.string(),
      unitPrice: z.number().nullable(),
      minimumOrderQuantity: z.number().nullable(),
      incrementalOrderQuantity: z.number().nullable(),
      movThreshold: z.number().nullable(),
    }),
  ),
});

export type FulfillmentCandidatesResponse = z.infer<
  typeof FulfillmentCandidatesResponseSchema
>;

// ─── Per-need decision-support insights (for the composite-builder modal) ──

export const GetNeedInsightsInputSchema = z.object({
  suggestionId: z.string().uuid(),
});

export type GetNeedInsightsInput = z.infer<typeof GetNeedInsightsInputSchema>;

export const NeedInventoryRowSchema = z.object({
  warehouseId: z.string().uuid(),
  warehouseName: z.string(),
  divisionName: z.string().nullable(),
  type: z.string().nullable(),
  onHandQuantity: z.number(),
  reservedQuantity: z.number(),
  availableQuantity: z.number(),
  isDestination: z.boolean(),
});
export type NeedInventoryRow = z.infer<typeof NeedInventoryRowSchema>;

export const NeedSalesPointSchema = z.object({
  monthStart: z.string(),
  quantity: z.number(),
});
export type NeedSalesPoint = z.infer<typeof NeedSalesPointSchema>;

// One forward month of the demand-forecast curve (the forecasting engine's
// output, im_demand_forecasts). `quantity` is the projected demand; `lower`/
// `upper` are the optional confidence band (null until the engine emits one).
// `monthStart` = YYYY-MM-01, same convention as NeedSalesPoint so the two
// series stitch onto one continuous timeline in the chart.
export const ForecastPointSchema = z.object({
  monthStart: z.string(),
  quantity: z.number(),
  lower: z.number().nullable(),
  upper: z.number().nullable(),
});
export type ForecastPoint = z.infer<typeof ForecastPointSchema>;

export const GetNeedInsightsResponseSchema = z.object({
  suggestionId: z.string().uuid(),
  productId: z.string().uuid(),
  destinationWarehouseId: z.string().uuid(),
  popularityCode: z.string().nullable(),
  inventoryByWarehouse: z.array(NeedInventoryRowSchema),
  salesHistory: z.array(NeedSalesPointSchema),
  // Forward demand-forecast curve at the destination (active run only). Empty
  // when no forecast has been computed for this SKU×warehouse yet.
  forecast: z.array(ForecastPointSchema),
});
export type GetNeedInsightsResponse = z.infer<
  typeof GetNeedInsightsResponseSchema
>;

// Product-scoped variant of the buyer's-menu insights — same payload as
// GetNeedInsights minus `suggestionId`, driven directly by a (product, variant,
// destination-warehouse) tuple. Used where there is no replenishment suggestion
// to key off (e.g. the outgoing-transfers line drill-in).
export const GetProductInsightsInputSchema = z.object({
  productId: z.string().uuid(),
  destinationWarehouseId: z.string().uuid(),
  productVariantId: z.string().uuid().nullable().optional(),
});
export type GetProductInsightsInput = z.infer<
  typeof GetProductInsightsInputSchema
>;

export const GetProductInsightsResponseSchema = z.object({
  productId: z.string().uuid(),
  destinationWarehouseId: z.string().uuid(),
  popularityCode: z.string().nullable(),
  inventoryByWarehouse: z.array(NeedInventoryRowSchema),
  salesHistory: z.array(NeedSalesPointSchema),
  forecast: z.array(ForecastPointSchema),
});
export type GetProductInsightsResponse = z.infer<
  typeof GetProductInsightsResponseSchema
>;

// Standalone product sales-history fetch (not tied to a replenishment
// suggestion). Powers the item-detail page chart.
export const GetProductSalesHistoryInputSchema = z.object({
  productId: z.string().uuid(),
  // Legacy single-value form. `warehouseIds` is preferred and wins when both
  // are supplied; both accepted for back-compat with existing call sites.
  warehouseId: z.string().uuid().nullable().optional(),
  warehouseIds: z.array(z.string().uuid()).nullable().optional(),
  monthsBack: z.number().int().min(1).max(60).optional().default(12),
});
export type GetProductSalesHistoryInput = z.infer<
  typeof GetProductSalesHistoryInputSchema
>;

export const GetProductSalesHistoryResponseSchema = z.object({
  productId: z.string().uuid(),
  warehouseId: z.string().uuid().nullable(),
  salesHistory: z.array(NeedSalesPointSchema),
  // Forward demand-forecast curve over the same warehouse scope (active run per
  // warehouse, summed). Empty when no forecast exists for the selection.
  forecast: z.array(ForecastPointSchema),
});
export type GetProductSalesHistoryResponse = z.infer<
  typeof GetProductSalesHistoryResponseSchema
>;

// Trailing N-month movement history at a (product, optional warehouse). 4
// series: outbound = sales + transfers-out; inbound = buys + transfers-in.
// Same scoping rules as the sales-history endpoint.
export const GetProductMovementHistoryInputSchema = z.object({
  productId: z.string().uuid(),
  warehouseId: z.string().uuid().nullable().optional(),
  warehouseIds: z.array(z.string().uuid()).nullable().optional(),
  monthsBack: z.number().int().min(1).max(60).optional().default(12),
});
export type GetProductMovementHistoryInput = z.infer<
  typeof GetProductMovementHistoryInputSchema
>;

export const MovementHistoryPointSchema = z.object({
  monthStart: z.string(),
  sales: z.number(),
  transfersOut: z.number(),
  buys: z.number(),
  transfersIn: z.number(),
});
export type MovementHistoryPoint = z.infer<typeof MovementHistoryPointSchema>;

// One row per (warehouse, month) for the month×location breakdown table.
// `warehouseId` is null for movements not tied to a location (e.g. AS400 buys
// with no ship-to warehouse), surfaced as an "unassigned" location row.
export const MovementByLocationRowSchema = z.object({
  warehouseId: z.string().uuid().nullable(),
  warehouseName: z.string().nullable(),
  divisionName: z.string().nullable(),
  monthStart: z.string(),
  sales: z.number(),
  transfersOut: z.number(),
  buys: z.number(),
  transfersIn: z.number(),
});
export type MovementByLocationRow = z.infer<typeof MovementByLocationRowSchema>;

export const GetProductMovementHistoryResponseSchema = z.object({
  productId: z.string().uuid(),
  warehouseId: z.string().uuid().nullable(),
  movements: z.array(MovementHistoryPointSchema),
  byLocation: z.array(MovementByLocationRowSchema),
});
export type GetProductMovementHistoryResponse = z.infer<
  typeof GetProductMovementHistoryResponseSchema
>;

// End-of-month historical on-hand at a (product, optional set of warehouses).
// Used as an overlay line on the sales + movement charts.
export const GetProductOnHandHistoryInputSchema = z.object({
  productId: z.string().uuid(),
  warehouseIds: z.array(z.string().uuid()).nullable().optional(),
  monthsBack: z.number().int().min(1).max(60).optional().default(12),
});
export type GetProductOnHandHistoryInput = z.infer<
  typeof GetProductOnHandHistoryInputSchema
>;

export const OnHandHistoryPointSchema = z.object({
  monthStart: z.string(),
  onHand: z.number(),
});
export type OnHandHistoryPoint = z.infer<typeof OnHandHistoryPointSchema>;

export const GetProductOnHandHistoryResponseSchema = z.object({
  productId: z.string().uuid(),
  onHand: z.array(OnHandHistoryPointSchema),
});
export type GetProductOnHandHistoryResponse = z.infer<
  typeof GetProductOnHandHistoryResponseSchema
>;

// ─── Confirm carts → create POs + TOs ───────────────────────────────────────

export const ConfirmCartsInputSchema = z.object({
  // The vendor carts the buyer signed off on. Cart = (supplier, ship-to).
  carts: z
    .array(
      z.object({
        supplierId: z.string().uuid(),
        shipToWarehouseId: z.string().uuid(),
      }),
    )
    .min(1),
  divisionId: z.string().uuid().optional(),
  divisionIds: z.array(z.string().uuid()).optional(),
  // Suggestions the buyer locally toggled OUT of the cart this session. The
  // board's include-toggle is buyer-controlled and separate from the engine's
  // recommendedAction, so creation must honor these exclusions.
  excludeSuggestionIds: z.array(z.string().uuid()).optional(),
  // When true, also create POs for carts below the vendor's hard MOV. When
  // false (default), below-MOV carts are skipped and returned in
  // `blockedBelowMov` so the UI can warn and re-confirm with acknowledgement.
  acknowledgeBelowMov: z.boolean().optional().default(false),
});

export type ConfirmCartsInput = z.infer<typeof ConfirmCartsInputSchema>;

export const ConfirmCartsResponseSchema = z.object({
  // Carts skipped because cartTotal < hard MOV and acknowledgeBelowMov=false.
  blockedBelowMov: z.array(
    z.object({
      supplierId: z.string().uuid(),
      supplierName: z.string().nullable(),
      shipToWarehouseId: z.string().uuid(),
      shipToWarehouseName: z.string().nullable(),
      cartTotal: z.number(),
      movThreshold: z.number(),
    }),
  ),
  // Model D: these are DRAFT orders (im_draft_purchase_orders / _transfer_orders).
  // `id` is the draft id; there is no AS400 number yet (assigned on ack), so no
  // externalId/transferNumber here — they no longer use a placeholder scheme.
  createdPurchaseOrders: z.array(
    z.object({
      id: z.string().uuid(),
      supplierId: z.string().uuid(),
      supplierName: z.string().nullable(),
      shipToWarehouseId: z.string().uuid().nullable(),
      lineCount: z.number(),
      totalAmount: z.number(),
    }),
  ),
  createdTransferOrders: z.array(
    z.object({
      id: z.string().uuid(),
      sourceWarehouseId: z.string().uuid().nullable(),
      destinationWarehouseId: z.string().uuid().nullable(),
      lineCount: z.number(),
    }),
  ),
  suggestionsConfirmed: z.number(),
});

export type ConfirmCartsResponse = z.infer<typeof ConfirmCartsResponseSchema>;

// ─── Confirm vendorless-transfer carts → create DRAFT TOs ───────────────────
// The supplier-anchored confirmCarts can't mint these (no vendor). A transfer
// "cart" is (source → destination).

export const ConfirmTransfersInputSchema = z.object({
  carts: z
    .array(
      z.object({
        sourceWarehouseId: z.string().uuid(),
        destinationWarehouseId: z.string().uuid(),
      }),
    )
    .min(1),
  divisionId: z.string().uuid().optional(),
  divisionIds: z.array(z.string().uuid()).optional(),
  warehouseIds: z.array(z.string().uuid()).optional(),
  // Suggestions the buyer locally toggled OUT of the cart this session.
  excludeSuggestionIds: z.array(z.string().uuid()).optional(),
});

export type ConfirmTransfersInput = z.infer<typeof ConfirmTransfersInputSchema>;

export const ConfirmTransfersResponseSchema = z.object({
  // Model D: DRAFT transfer orders (im_draft_transfer_orders). `id` is the draft
  // id; AS400's transfer number is assigned later on ack.
  createdTransferOrders: z.array(
    z.object({
      id: z.string().uuid(),
      sourceWarehouseId: z.string().uuid().nullable(),
      destinationWarehouseId: z.string().uuid().nullable(),
      lineCount: z.number(),
    }),
  ),
  suggestionsConfirmed: z.number(),
});

export type ConfirmTransfersResponse = z.infer<
  typeof ConfirmTransfersResponseSchema
>;

// ─── Replenishment-created orders (procurement-module list view) ─────────────
//
// Per-type, server-side paginated/filterable/sortable list endpoints (one for
// POs, one for TOs). The orders page toggles between them. Mirrors the AR
// invoices list shape: filters + sortBy allowlist + limit/offset → { rows,
// pagination }.

export const ReplenishmentOrdersPaginationSchema = z.object({
  total: z.int().nonnegative(),
  limit: z.int().positive(),
  offset: z.int().nonnegative(),
});

const replenishmentOrdersBaseInput = {
  divisionId: z.string().uuid().optional(),
  divisionIds: z.array(z.string().uuid()).optional(),
  status: z.array(z.string()).optional(),
  search: z.string().optional(),
  sortDirection: z.enum(["asc", "desc"]).optional().prefault("desc"),
  limit: z.int().min(1).max(200).optional().prefault(50),
  offset: z.int().min(0).optional().prefault(0),
};

export const ReplenishmentPurchaseOrderSortBySchema = z.enum([
  "trackingNo",
  "status",
  "supplierName",
  "shipToWarehouseName",
  "lineCount",
  "totalAmount",
  "createdByUserName",
  "orderDate",
  "createdAt",
]);

export const ReplenishmentTransferOrderSortBySchema = z.enum([
  "trackingNo",
  "status",
  "sourceWarehouseName",
  "destinationWarehouseName",
  "lineCount",
  "createdByUserName",
  "orderDate",
  "createdAt",
]);

export const ListReplenishmentPurchaseOrdersInputSchema = z.object({
  ...replenishmentOrdersBaseInput,
  sortBy:
    ReplenishmentPurchaseOrderSortBySchema.optional().prefault("createdAt"),
});
// Provenance of a draft transfer order: `replenishment` = a buyer confirmed it
// on the board; `replenishment_auto` = Phase 5 auto-staged a branch→hub transfer
// (opt-out). The transfers portal filters to `replenishment_auto` to show the
// "staged, going out unless cancelled" set.
export const ReplenishmentTransferOriginSystemSchema = z.enum([
  "replenishment",
  "replenishment_auto",
]);

export const ListReplenishmentTransferOrdersInputSchema = z.object({
  ...replenishmentOrdersBaseInput,
  originSystem: ReplenishmentTransferOriginSystemSchema.optional(),
  sortBy:
    ReplenishmentTransferOrderSortBySchema.optional().prefault("createdAt"),
});

export type ListReplenishmentPurchaseOrdersInput = z.infer<
  typeof ListReplenishmentPurchaseOrdersInputSchema
>;
export type ListReplenishmentTransferOrdersInput = z.infer<
  typeof ListReplenishmentTransferOrdersInputSchema
>;

// Model D unified order row: `kind` says whether `id` is a draft (pre-ack, in
// im_draft_*) or a real acked order (in the real PO/TO table, linked back via
// draft_*_order_id). `status` is the buyer-facing lifecycle:
//   pending_approval | pending | submitted | acked | errored.
// `pending_approval` is a draft PO held for role/$-authority approval (PO only).
// `trackingNo` shows once submitted; `externalId` is AS400's number (acked
// only); `outboundError` is set on `errored`.
export const ReplenishmentOrderKindSchema = z.enum(["draft", "real"]);
export const ReplenishmentOrderStatusSchema = z.enum([
  // Held for role/$-authority approval (created on Confirm, not yet released to
  // the outbound push). See docs/procurement/po-approval-workflow.md.
  "pending_approval",
  // An approval gate rejected the held draft (terminal, PO-only) — never picked
  // up for outbound.
  "rejected",
  "pending",
  "submitted",
  "acked",
  "errored",
]);

// Rollup of a held draft's approval bag (see docs/procurement/po-approval-workflow.md):
// `state` is the overall outcome; `decidedBy*`/`decidedAt`/`comment` are the latest
// terminal decision, for a "who & when" hover. Null when the PO never entered approval.
export const PurchaseOrderApprovalSummarySchema = z.object({
  state: z.enum(["awaiting", "approved", "rejected"]),
  decidedByName: z.string().nullable(),
  decidedAt: z.string().nullable(),
  comment: z.string().nullable(),
});

export const ReplenishmentPurchaseOrderRowSchema = z.object({
  id: z.string().uuid(),
  kind: ReplenishmentOrderKindSchema,
  status: ReplenishmentOrderStatusSchema,
  trackingNo: z.string().nullable(),
  externalId: z.string().nullable(),
  outboundError: z.string().nullable(),
  supplierId: z.string().uuid().nullable(),
  supplierName: z.string().nullable(),
  shipToWarehouseId: z.string().uuid().nullable(),
  shipToWarehouseName: z.string().nullable(),
  lineCount: z.number(),
  totalAmount: z.number().nullable(),
  createdByUserId: z.string().uuid().nullable(),
  createdByUserName: z.string().nullable(),
  orderDate: z.string().nullable(),
  createdAt: z.string(),
  approval: PurchaseOrderApprovalSummarySchema.nullable(),
});

export const ReplenishmentTransferOrderRowSchema = z.object({
  id: z.string().uuid(),
  kind: ReplenishmentOrderKindSchema,
  status: ReplenishmentOrderStatusSchema,
  trackingNo: z.string().nullable(),
  externalId: z.string().nullable(),
  outboundError: z.string().nullable(),
  sourceWarehouseId: z.string().uuid().nullable(),
  sourceWarehouseName: z.string().nullable(),
  destinationWarehouseId: z.string().uuid().nullable(),
  destinationWarehouseName: z.string().nullable(),
  originSystem: ReplenishmentTransferOriginSystemSchema.nullable(),
  lineCount: z.number(),
  createdByUserId: z.string().uuid().nullable(),
  createdByUserName: z.string().nullable(),
  orderDate: z.string().nullable(),
  createdAt: z.string(),
});

export const ListReplenishmentPurchaseOrdersResponseSchema = z.object({
  rows: z.array(ReplenishmentPurchaseOrderRowSchema),
  pagination: ReplenishmentOrdersPaginationSchema,
});
export const ListReplenishmentTransferOrdersResponseSchema = z.object({
  rows: z.array(ReplenishmentTransferOrderRowSchema),
  pagination: ReplenishmentOrdersPaginationSchema,
});

export type ListReplenishmentPurchaseOrdersResponse = z.infer<
  typeof ListReplenishmentPurchaseOrdersResponseSchema
>;
export type ListReplenishmentTransferOrdersResponse = z.infer<
  typeof ListReplenishmentTransferOrdersResponseSchema
>;
export type ReplenishmentPurchaseOrderRow = z.infer<
  typeof ReplenishmentPurchaseOrderRowSchema
>;
export type ReplenishmentTransferOrderRow = z.infer<
  typeof ReplenishmentTransferOrderRowSchema
>;

// ─── PO approval progress (workflow-state pipeline) ──────────────────────────
// Tier-by-tier projection of a held draft PO's approval, mirroring the OMS-v3
// WorkflowPipeline: structure from the owning workflow run's definition,
// decisions from the im_draft_purchase_order_approvals store. See
// backend/src/core/services/procurement/draftPurchaseOrderWorkflowState.ts.

export const GetDraftPurchaseOrderWorkflowStateInputSchema = z.object({
  draftPurchaseOrderId: z.string().uuid(),
});

// Only approval gates a human was (or is) involved in are listed: awaiting
// gates plus human-decided ones. Non-approval steps, unreached gates, and
// auto-approved gates are omitted by the projection.
export const DraftPurchaseOrderWorkflowStateNodeSchema = z.object({
  id: z.string(),
  label: z.string(),
  sub: z.string(),
  role: z.string().nullable(),
  assignee: z.string().nullable(),
  // `pending` = raised, awaiting a decision.
  decision: z.enum([
    "approved",
    "rejected",
    "escalated",
    "timed_out",
    "pending",
  ]),
  // When the gate was decided (terminal) or raised (pending). ISO timestamp.
  decidedAt: z.string().nullable(),
  // The decider's free-text comment for this gate, or null.
  comment: z.string().nullable(),
  // Entered via an escalation edge (named escalation path / timed_out socket)
  // — the UI tags these "Escalation".
  enteredViaEscalation: z.boolean(),
});

export const DraftPurchaseOrderWorkflowStateSchema = z.object({
  hasWorkflow: z.boolean(),
  nodes: z.array(DraftPurchaseOrderWorkflowStateNodeSchema),
  // Counts cover only the listed gates (auto-approved ones are excluded).
  approvalsTotal: z.number(),
  approvalsDone: z.number(),
  rejected: z.boolean(),
  rejectionComment: z.string().nullable(),
  // Linked run + its workflow lineage id, for an admin "view run" deep link.
  workflowRunId: z.string().nullable(),
  workflowId: z.string().nullable(),
  // The raised-but-undecided gate's step run, backing the orders-board
  // "Decide in inbox" deep link (`/inbox?selId=approval-<stepRunId>`).
  // Null when no gate is currently awaiting a decision.
  pendingStepRunId: z.string().nullable(),
  // Whether the CALLER would see the pending gate in their inbox (approver
  // team member / configured user / OOO delegate / admin) — gates the CTA so
  // it never links someone to an inbox item they can't find. False when no
  // gate is pending.
  viewerCanDecide: z.boolean(),
});

export type GetDraftPurchaseOrderWorkflowStateInput = z.infer<
  typeof GetDraftPurchaseOrderWorkflowStateInputSchema
>;
export type DraftPurchaseOrderWorkflowState = z.infer<
  typeof DraftPurchaseOrderWorkflowStateSchema
>;

// ─── Draft PO detail (header + lines) ─────────────────────────────────────────
// One shared read model behind both PO detail surfaces — the inbox approval
// pane and the orders-board drill-in (docs/procurement/po-approval-workflow.md
// §7). Approval progress is deliberately NOT here; compose with
// `getDraftPurchaseOrderWorkflowState`.

export const GetDraftPurchaseOrderDetailInputSchema = z.object({
  draftPurchaseOrderId: z.string().uuid(),
});

export const DraftPurchaseOrderDetailLineSchema = z.object({
  id: z.string().uuid(),
  lineNumber: z.number().nullable(),
  productId: z.string().uuid().nullable(),
  productSku: z.string().nullable(),
  productName: z.string().nullable(),
  description: z.string().nullable(),
  quantity: z.number(),
  unitOfMeasure: z.string().nullable(),
  unitPrice: z.number().nullable(),
  totalPrice: z.number().nullable(),
});

export const DraftPurchaseOrderDetailSchema = z.object({
  id: z.string().uuid(),
  status: ReplenishmentOrderStatusSchema,
  trackingNo: z.string().nullable(),
  supplierId: z.string().uuid().nullable(),
  supplierName: z.string().nullable(),
  shipToWarehouseId: z.string().uuid().nullable(),
  shipToWarehouseName: z.string().nullable(),
  totalAmount: z.number().nullable(),
  orderDate: z.string().nullable(),
  createdByUserId: z.string().uuid().nullable(),
  createdByUserName: z.string().nullable(),
  createdAt: z.string(),
  lines: z.array(DraftPurchaseOrderDetailLineSchema),
});

export type GetDraftPurchaseOrderDetailInput = z.infer<
  typeof GetDraftPurchaseOrderDetailInputSchema
>;
export type DraftPurchaseOrderDetailLine = z.infer<
  typeof DraftPurchaseOrderDetailLineSchema
>;
export type DraftPurchaseOrderDetail = z.infer<
  typeof DraftPurchaseOrderDetailSchema
>;

// ─── Retry / recall a pre-ack draft order ────────────────────────────────────
// `kind` selects the draft table; `id` is the draft order id.

export const ReplenishmentDraftKindSchema = z.enum(["po", "to"]);

export const RetryReplenishmentOrderInputSchema = z.object({
  kind: ReplenishmentDraftKindSchema,
  id: z.string().uuid(),
});
export const RetryReplenishmentOrderResponseSchema = z.object({
  id: z.string().uuid(),
  status: ReplenishmentOrderStatusSchema,
});
export type RetryReplenishmentOrderInput = z.infer<
  typeof RetryReplenishmentOrderInputSchema
>;
export type RetryReplenishmentOrderResponse = z.infer<
  typeof RetryReplenishmentOrderResponseSchema
>;

export const RecallReplenishmentOrderInputSchema = z.object({
  kind: ReplenishmentDraftKindSchema,
  id: z.string().uuid(),
});
export const RecallReplenishmentOrderResponseSchema = z.object({
  id: z.string().uuid(),
  // Suggestions returned to `pending` so they can be re-confirmed.
  suggestionsReverted: z.number(),
});
export type RecallReplenishmentOrderInput = z.infer<
  typeof RecallReplenishmentOrderInputSchema
>;
export type RecallReplenishmentOrderResponse = z.infer<
  typeof RecallReplenishmentOrderResponseSchema
>;

// Cancel an auto-staged transfer from the transfers portal. `block` also flags
// the destination branch's `auto_transfer_blocked` policy for the order's
// products, so the cancelled transfer is not silently re-staged next run.
export const CancelStagedTransferInputSchema = z.object({
  id: z.string().uuid(),
  block: z.boolean(),
});
export const CancelStagedTransferResponseSchema = z.object({
  id: z.string().uuid(),
  suggestionsReverted: z.number(),
  productsBlocked: z.number(),
});
export type CancelStagedTransferInput = z.infer<
  typeof CancelStagedTransferInputSchema
>;
export type CancelStagedTransferResponse = z.infer<
  typeof CancelStagedTransferResponseSchema
>;

// Line items of a single (draft) transfer order — for the transfers-portal
// row drill-in.
export const GetReplenishmentTransferOrderLinesInputSchema = z.object({
  id: z.string().uuid(),
});
export const ReplenishmentTransferOrderLineSchema = z.object({
  id: z.string().uuid(),
  productId: z.string().uuid().nullable(),
  productVariantId: z.string().uuid().nullable(),
  productSku: z.string().nullable(),
  productName: z.string().nullable(),
  quantity: z.number(),
  unitOfMeasure: z.string().nullable(),
});
export const GetReplenishmentTransferOrderLinesResponseSchema = z.object({
  id: z.string().uuid(),
  sourceWarehouseName: z.string().nullable(),
  destinationWarehouseName: z.string().nullable(),
  lines: z.array(ReplenishmentTransferOrderLineSchema),
});
export type GetReplenishmentTransferOrderLinesInput = z.infer<
  typeof GetReplenishmentTransferOrderLinesInputSchema
>;
export type GetReplenishmentTransferOrderLinesResponse = z.infer<
  typeof GetReplenishmentTransferOrderLinesResponseSchema
>;
export type ReplenishmentTransferOrderLine = z.infer<
  typeof ReplenishmentTransferOrderLineSchema
>;

// ─── SO Status page (bulk lookup → header + line items + dependent POs/TOs) ──

export const GetSalesOrdersProcurementStatusInputSchema = z.object({
  // SO tokens: internal # or customer PO #. Bulk lookup (paste "1,2,3,4").
  queries: z.array(z.string()).max(50),
});

export const SalesOrderHeaderSchema = z.object({
  id: z.string().uuid(),
  internalId: z.number(),
  customerPurchaseOrderNumber: z.string().nullable(),
  status: z.string(),
  customerName: z.string().nullable(),
  orderDate: z.string(),
  deliveryDate: z.string().nullable(),
  totalAmount: z.number().nullable(),
});
export const SalesOrderLineItemRowSchema = z.object({
  id: z.string().uuid(),
  lineItemNumber: z.number().nullable(),
  productName: z.string().nullable(),
  productSku: z.string().nullable(),
  quantity: z.number().nullable(),
  unitPrice: z.number().nullable(),
  lineTotal: z.number().nullable(),
});
export const SalesOrderStatusResultSchema = z.object({
  query: z.string(),
  salesOrder: SalesOrderHeaderSchema.nullable(),
  lineItems: z.array(SalesOrderLineItemRowSchema),
  purchaseOrders: z.array(
    z.object({
      id: z.string().uuid(),
      externalId: z.string(),
      status: z.string(),
      supplierName: z.string().nullable(),
    }),
  ),
  transferOrders: z.array(
    z.object({
      id: z.string().uuid(),
      transferNumber: z.string(),
      status: z.string(),
      sourceWarehouseName: z.string().nullable(),
      destinationWarehouseName: z.string().nullable(),
    }),
  ),
});
export const GetSalesOrdersProcurementStatusResponseSchema = z.object({
  results: z.array(SalesOrderStatusResultSchema),
});

export type GetSalesOrdersProcurementStatusInput = z.infer<
  typeof GetSalesOrdersProcurementStatusInputSchema
>;
export type GetSalesOrdersProcurementStatusResponse = z.infer<
  typeof GetSalesOrdersProcurementStatusResponseSchema
>;
export type SalesOrderStatusResult = z.infer<
  typeof SalesOrderStatusResultSchema
>;

// Paginated SO Status table (mirrors the PO Status table).
export const ListSalesOrderStatusesInputSchema = z.object({
  internalIds: z.array(z.number().int()).optional(),
  status: z.array(z.string()).optional(),
  orderDateFrom: z.string().optional(),
  orderDateTo: z.string().optional(),
  sortBy: z
    .enum(["internalId", "status", "orderDate", "totalAmount", "customerName"])
    .optional()
    .prefault("orderDate"),
  sortDirection: z.enum(["asc", "desc"]).optional().prefault("desc"),
  limit: z.int().min(1).max(200).optional().prefault(25),
  offset: z.int().min(0).optional().prefault(0),
});
export const SalesOrderStatusRowSchema = z.object({
  id: z.string().uuid(),
  internalId: z.number(),
  customerPurchaseOrderNumber: z.string().nullable(),
  customerName: z.string().nullable(),
  status: z.string(),
  orderDate: z.string(),
  totalAmount: z.number().nullable(),
  dependentPoCount: z.number(),
  dependentToCount: z.number(),
});
export const ListSalesOrderStatusesResponseSchema = z.object({
  rows: z.array(SalesOrderStatusRowSchema),
  pagination: ReplenishmentOrdersPaginationSchema,
});
export type ListSalesOrderStatusesInput = z.infer<
  typeof ListSalesOrderStatusesInputSchema
>;
export type ListSalesOrderStatusesResponse = z.infer<
  typeof ListSalesOrderStatusesResponseSchema
>;
export type SalesOrderStatusRow = z.infer<typeof SalesOrderStatusRowSchema>;

// ─── TO Status page (bulk lookup → TO + driving SOs + sibling POs) ───────────
//
// Inverse of the SO Status page: a transfer order *serves* the demand of the
// sales order(s) whose replenishment suggestions it fulfills. From those same
// driving SOs we also surface the "sibling" purchase orders (POs created for
// the same demand). The replenishment suggestion is the join:
//   TO  ← suggestion.transfer_order_id ; suggestion.trigger_sales_order_id → SO
//   SO  → suggestion.trigger_sales_order_id ; suggestion.purchase_order_id  → PO

export const GetTransferOrdersProcurementStatusInputSchema = z.object({
  // TO tokens: transfer # (alphanumeric) or internal # (numeric). Bulk lookup.
  queries: z.array(z.string()).max(50),
});

export const TransferOrderStatusHeaderSchema = z.object({
  id: z.string().uuid(),
  internalId: z.number(),
  transferNumber: z.string(),
  status: z.string(),
  sourceWarehouseName: z.string().nullable(),
  destinationWarehouseName: z.string().nullable(),
  orderDate: z.string().nullable(),
});
export const TransferOrderStatusResultSchema = z.object({
  query: z.string(),
  transferOrder: TransferOrderStatusHeaderSchema.nullable(),
  salesOrders: z.array(
    z.object({
      id: z.string().uuid(),
      internalId: z.number(),
      status: z.string(),
      customerName: z.string().nullable(),
    }),
  ),
  purchaseOrders: z.array(
    z.object({
      id: z.string().uuid(),
      externalId: z.string(),
      status: z.string(),
      supplierName: z.string().nullable(),
    }),
  ),
});
export const GetTransferOrdersProcurementStatusResponseSchema = z.object({
  results: z.array(TransferOrderStatusResultSchema),
});

export type GetTransferOrdersProcurementStatusInput = z.infer<
  typeof GetTransferOrdersProcurementStatusInputSchema
>;
export type GetTransferOrdersProcurementStatusResponse = z.infer<
  typeof GetTransferOrdersProcurementStatusResponseSchema
>;
export type TransferOrderStatusResult = z.infer<
  typeof TransferOrderStatusResultSchema
>;

// Paginated TO Status table (mirrors the SO Status table).
export const ListTransferOrderStatusesInputSchema = z.object({
  // Search tokens: transfer # (alphanumeric) or internal # (numeric).
  search: z.array(z.string()).optional(),
  status: z.array(z.string()).optional(),
  // Filter by where the TO came from, e.g. "replenishment" or "as400_gains".
  originSystems: z.array(z.string()).optional(),
  // Scope to TO destination warehouses in these divisions.
  divisionIds: z.array(z.string().uuid()).optional(),
  orderDateFrom: z.string().optional(),
  orderDateTo: z.string().optional(),
  sortBy: z
    .enum(["internalId", "transferNumber", "status", "orderDate"])
    .optional()
    .prefault("orderDate"),
  sortDirection: z.enum(["asc", "desc"]).optional().prefault("desc"),
  limit: z.int().min(1).max(200).optional().prefault(25),
  offset: z.int().min(0).optional().prefault(0),
});
export const TransferOrderStatusRowSchema = z.object({
  id: z.string().uuid(),
  internalId: z.number(),
  transferNumber: z.string(),
  status: z.string(),
  sourceWarehouseName: z.string().nullable(),
  destinationWarehouseName: z.string().nullable(),
  orderDate: z.string().nullable(),
  lineCount: z.number(),
  createdByUserName: z.string().nullable(),
  servesSoCount: z.number(),
  siblingPoCount: z.number(),
});
export const ListTransferOrderStatusesResponseSchema = z.object({
  rows: z.array(TransferOrderStatusRowSchema),
  pagination: ReplenishmentOrdersPaginationSchema,
});
export type ListTransferOrderStatusesInput = z.infer<
  typeof ListTransferOrderStatusesInputSchema
>;
export type ListTransferOrderStatusesResponse = z.infer<
  typeof ListTransferOrderStatusesResponseSchema
>;
export type TransferOrderStatusRow = z.infer<
  typeof TransferOrderStatusRowSchema
>;

// ─── Contract ───────────────────────────────────────────────────────────────

export const replenishmentSuggestionsContracts = {
  listSupplierSummaries: oc
    .input(ListSupplierSummariesInputSchema)
    .output(ListSupplierSummariesResponseSchema)
    .route({
      method: "GET",
      summary:
        "List supplier summaries with aggregated replenishment suggestion metrics.",
    }),
  getSupplierDetail: oc
    .input(GetSupplierDetailInputSchema)
    .output(GetSupplierDetailResponseSchema)
    .route({
      method: "GET",
      summary:
        "Get replenishment suggestions for a specific supplier with top-ranked option details.",
    }),
  getSuggestionDetail: oc
    .input(GetSuggestionDetailInputSchema)
    .output(GetSuggestionDetailResponseSchema)
    .route({
      method: "GET",
      summary:
        "Get a single replenishment suggestion with all its fulfillment options.",
    }),
  listTransferSummaries: oc
    .input(ListTransferSummariesInputSchema)
    .output(ListTransferSummariesResponseSchema)
    .route({
      method: "GET",
      summary:
        "List transfer-only replenishment suggestions (not grouped by supplier).",
    }),
  listNoSourceSuggestions: oc
    .input(ListNoSourceSuggestionsInputSchema)
    .output(ListNoSourceSuggestionsResponseSchema)
    .route({
      method: "GET",
      summary:
        "List replenishment suggestions with no fulfillment option (no transfer source and no vendor mapping).",
    }),
  listSupplierSummariesWithCarts: oc
    .input(ListSupplierSummariesInputSchema)
    .output(ListSupplierSummariesWithCartsResponseSchema)
    .route({
      method: "GET",
      summary:
        "List supplier summaries with nested per-cart (supplier × ship-to) breakdowns.",
    }),
  getCartLines: oc
    .input(GetCartLinesInputSchema)
    .output(GetCartLinesResponseSchema)
    .route({
      method: "GET",
      summary:
        "Get paginated suggestion lines for a specific cart (supplier × ship-to warehouse).",
    }),
  getSupplierAssignments: oc
    .input(GetSupplierAssignmentsInputSchema)
    .output(GetSupplierAssignmentsResponseSchema)
    .route({
      method: "GET",
      summary:
        "Get all suggestion lines and cart assignments for a supplier (unpaginated, for assignment UI).",
    }),
  listVendorlessTransferSummaries: oc
    .input(ListVendorlessTransferSummariesInputSchema)
    .output(ListVendorlessTransferSummariesResponseSchema)
    .route({
      method: "GET",
      summary:
        "List vendorless-transfer summaries grouped by source warehouse (transfer board strip cards).",
    }),
  getSourceTransferAssignments: oc
    .input(GetSourceTransferAssignmentsInputSchema)
    .output(GetSourceTransferAssignmentsResponseSchema)
    .route({
      method: "GET",
      summary:
        "Get all vendorless-transfer lines + destination carts for one source warehouse (transfer assignment UI).",
    }),
  applyOption: oc
    .input(ApplyOptionInputSchema)
    .output(ApplyOptionResponseSchema)
    .route({
      method: "POST",
      summary:
        "Set the chosen fulfillment option for a replenishment suggestion. Idempotent; does not change status.",
    }),
  setSuggestionStatus: oc
    .input(SetSuggestionStatusInputSchema)
    .output(SetSuggestionStatusResponseSchema)
    .route({
      method: "POST",
      summary:
        "Trash (reject) or restore (pending) a replenishment suggestion. Only flips between pending and rejected.",
    }),
  override: oc
    .input(OverrideOptionInputSchema)
    .output(OverrideOptionResponseSchema)
    .route({
      method: "POST",
      summary:
        "Add a buyer-authored (source:'user') fulfillment option to a suggestion with custom action/quantity/vendor/source, and optionally make it the chosen option.",
    }),
  composePlan: oc
    .input(ComposePlanInputSchema)
    .output(ComposePlanResponseSchema)
    .route({
      method: "POST",
      summary:
        "Compose a multi-leg buyer plan (e.g. 'transfer 5 from A + 3 from B + buy 2'). Writes one source:'user' option with N legs and (by default) sets it as the suggestion's chosen option. Supersedes the single-leg override for the composite-builder UX.",
    }),
  getFulfillmentCandidates: oc
    .input(FulfillmentCandidatesInputSchema)
    .output(FulfillmentCandidatesResponseSchema)
    .route({
      method: "GET",
      summary:
        "For a product + destination, list transfer-source warehouses (any with on-hand, ignoring surplus) and buy vendors (with price + MOV) — the choices for a buyer override.",
    }),
  refresh: oc
    .input(z.object({}))
    .output(
      z.object({
        batchId: z.string().uuid(),
        needsDetected: z.number(),
        suggestionsCreated: z.number(),
        suggestionsUpdated: z.number(),
        suggestionsSkipped: z.number(),
        durationMs: z.number(),
      }),
    )
    .route({
      method: "POST",
      summary:
        "Re-run the replenishment engine for the caller's organization. Admin only.",
    }),
  getNeedInsights: oc
    .input(GetNeedInsightsInputSchema)
    .output(GetNeedInsightsResponseSchema)
    .route({
      method: "GET",
      summary:
        "Per-need decision-support data: per-warehouse inventory snapshots, the destination's popularity code, and 12-month sales history at the destination. Powers the composite-builder modal.",
    }),
  getProductInsights: oc
    .input(GetProductInsightsInputSchema)
    .output(GetProductInsightsResponseSchema)
    .route({
      method: "GET",
      summary:
        "Product-scoped buyer's-menu insights (per-warehouse inventory, destination popularity, sales history + forward forecast) driven by a (product, variant, destination-warehouse) tuple instead of a replenishment suggestion. Powers the outgoing-transfers line drill-in.",
    }),
  getProductSalesHistory: oc
    .input(GetProductSalesHistoryInputSchema)
    .output(GetProductSalesHistoryResponseSchema)
    .route({
      method: "GET",
      summary:
        "N-month customer-facing sales history for a product, optionally scoped to a single warehouse. Used by the item detail page sales chart and shared with the replenishment need-insights modal.",
    }),
  getProductMovementHistory: oc
    .input(GetProductMovementHistoryInputSchema)
    .output(GetProductMovementHistoryResponseSchema)
    .route({
      method: "GET",
      summary:
        "N-month outbound (sales + transfers-out) and inbound (buys + transfers-in) movement history for a product, optionally scoped to a set of warehouses. Powers the item-detail movement chart.",
    }),
  getProductOnHandHistory: oc
    .input(GetProductOnHandHistoryInputSchema)
    .output(GetProductOnHandHistoryResponseSchema)
    .route({
      method: "GET",
      summary:
        "End-of-month historical on-hand for a product across an optional set of warehouses (sums the latest snapshot ≤ last-day-of-month per warehouse, then aggregates). Used as the on-hand overlay line on the sales + movement charts.",
    }),
  confirmCarts: oc
    .input(ConfirmCartsInputSchema)
    .output(ConfirmCartsResponseSchema)
    .route({
      method: "POST",
      summary:
        "Confirm one or more vendor carts: create DRAFT purchase orders (per supplier × ship-to) and transfer orders (per source → dest) from each committed suggestion's chosen-option legs, mark the suggestions accepted, and stamp the legs. Below-MOV carts are skipped unless acknowledgeBelowMov=true. Idempotent.",
    }),
  confirmTransfers: oc
    .input(ConfirmTransfersInputSchema)
    .output(ConfirmTransfersResponseSchema)
    .route({
      method: "POST",
      summary:
        "Confirm one or more vendorless-transfer carts (source → dest): create DRAFT transfer orders from each committed suggestion's transfer leg, mark them accepted, and stamp the legs. Idempotent.",
    }),
  listReplenishmentPurchaseOrders: oc
    .input(ListReplenishmentPurchaseOrdersInputSchema)
    .output(ListReplenishmentPurchaseOrdersResponseSchema)
    .route({
      method: "GET",
      summary:
        "Paginated/filterable/sortable list of replenishment purchase orders (Model D): pre-ack DRAFT POs (pending_approval/pending/submitted/errored) unified with the buyer-facing status, tracking#, and outbound error.",
    }),
  listReplenishmentTransferOrders: oc
    .input(ListReplenishmentTransferOrdersInputSchema)
    .output(ListReplenishmentTransferOrdersResponseSchema)
    .route({
      method: "GET",
      summary:
        "Paginated/filterable/sortable list of replenishment transfer orders (Model D): pre-ack DRAFT TOs (pending/submitted/errored) with buyer-facing status, tracking#, and outbound error.",
    }),
  getDraftPurchaseOrderWorkflowState: oc
    .input(GetDraftPurchaseOrderWorkflowStateInputSchema)
    .output(DraftPurchaseOrderWorkflowStateSchema)
    .route({
      method: "GET",
      summary:
        "Tier-by-tier approval-progress pipeline for a held draft PO: each gate's approver, decision, and who/when — merged from the linked workflow run's definition and the draft's recorded approvals.",
    }),
  getDraftPurchaseOrderDetail: oc
    .input(GetDraftPurchaseOrderDetailInputSchema)
    .output(DraftPurchaseOrderDetailSchema)
    .route({
      method: "GET",
      summary:
        "Header + line items of a single replenishment draft PO — the shared read model behind the inbox approval pane and the orders-board drill-in.",
    }),
  retryReplenishmentOrder: oc
    .input(RetryReplenishmentOrderInputSchema)
    .output(RetryReplenishmentOrderResponseSchema)
    .route({
      method: "POST",
      summary:
        "Retry an errored replenishment draft order: flip its status errored → pending so the outbound job picks it up again. Clears the prior outbound error.",
    }),
  recallReplenishmentOrder: oc
    .input(RecallReplenishmentOrderInputSchema)
    .output(RecallReplenishmentOrderResponseSchema)
    .route({
      method: "POST",
      summary:
        "Recall a pre-ack replenishment draft order: soft-delete the draft + its lines and return the linked suggestions to pending (clearing the draft link) so they can be re-confirmed. Rejected once the order is acked.",
    }),
  cancelStagedTransfer: oc
    .input(CancelStagedTransferInputSchema)
    .output(CancelStagedTransferResponseSchema)
    .route({
      method: "POST",
      summary:
        "Cancel an auto-staged branch transfer: recall the draft TO (revert its suggestions to pending) and, when block=true, flag the destination branch's auto_transfer_blocked policy for the order's products so it is not re-staged next run.",
    }),
  getReplenishmentTransferOrderLines: oc
    .input(GetReplenishmentTransferOrderLinesInputSchema)
    .output(GetReplenishmentTransferOrderLinesResponseSchema)
    .route({
      method: "GET",
      summary:
        "List the line items (product, quantity) of a single replenishment (draft) transfer order for the transfers-portal drill-in.",
    }),
  getSalesOrdersProcurementStatus: oc
    .input(GetSalesOrdersProcurementStatusInputSchema)
    .output(GetSalesOrdersProcurementStatusResponseSchema)
    .route({
      method: "GET",
      summary:
        "Bulk SO Status lookup: for each SO token (internal # or customer PO #), return the SO header + line items + the POs/TOs it depends on (via its replenishment suggestions) with live statuses.",
    }),
  listSalesOrderStatuses: oc
    .input(ListSalesOrderStatusesInputSchema)
    .output(ListSalesOrderStatusesResponseSchema)
    .route({
      method: "GET",
      summary:
        "Paginated/filterable/sortable sales-order list for the SO Status table, with dependent PO/TO counts per row.",
    }),
  getTransferOrdersProcurementStatus: oc
    .input(GetTransferOrdersProcurementStatusInputSchema)
    .output(GetTransferOrdersProcurementStatusResponseSchema)
    .route({
      method: "GET",
      summary:
        "Bulk TO Status lookup: for each TO token (transfer # or internal #), return the driving sales order(s) it serves and the sibling purchase order(s) from the same demand (via replenishment suggestions), with live statuses.",
    }),
  listTransferOrderStatuses: oc
    .input(ListTransferOrderStatusesInputSchema)
    .output(ListTransferOrderStatusesResponseSchema)
    .route({
      method: "GET",
      summary:
        "Paginated/filterable/sortable transfer-order list for the TO Status table, with served-SO / sibling-PO counts per row.",
    }),
  getForSalesOrder: oc
    .input(z.object({ salesOrderId: z.string().uuid() }))
    .output(
      z.object({
        items: z.array(
          z.object({
            triggerSalesOrderLineItemId: z.string().uuid(),
            suggestionId: z.string().uuid(),
            anchorSupplierId: z.string().uuid().nullable(),
            anchorSupplierName: z.string().nullable(),
            deliveryWarehouseId: z.string().uuid().nullable(),
            deliveryWarehouseName: z.string().nullable(),
            cartTotal: z.number().nullable(),
          }),
        ),
      }),
    )
    .route({
      method: "GET",
      summary:
        "For a sales order, return the pending replenishment suggestion (if any) that's triggered by each line item, including anchor supplier, delivery warehouse, and current cart total.",
    }),
} as const;
